/**
 * VRG-141 — capability → leaf-badge decomposition (the LOCKED badge rule).
 *
 * Source: supply/tag-technology-comparison.md Rev 5 (2026-07-22).
 *   - A badge is ALWAYS a leaf capability, one word — never a "Sense" badge, never
 *     an `A | B` compound. Sense is a filter/group + shared color family, not a badge.
 *   - Connect is shown on EVERY tag (neutral).
 *   - Vault renders ALONGSIDE Verify (authenticity always visible).
 *   - Family is encoded by color + icon: auth (Verify + Vault) = purple; Trace = own;
 *     Sense (Tamper/Temperature/Moisture/Pressure/Proximity) = own; Connect = neutral.
 */
import type { TagCapability, TagFormFactor } from './pricing'

export type CapabilityLeaf =
  | 'connect' | 'verify' | 'vault' | 'tamper' | 'trace'
  | 'temperature' | 'moisture' | 'pressure' | 'proximity'

/** Colour/icon family a leaf belongs to (drives the badge palette, not the label). */
export type CapabilityFamily = 'connect' | 'auth' | 'trace' | 'sense'

export const LEAF_LABEL: Record<CapabilityLeaf, string> = {
  connect: 'Connect', verify: 'Verify', vault: 'Vault', tamper: 'Tamper', trace: 'Trace',
  temperature: 'Temperature', moisture: 'Moisture', pressure: 'Pressure', proximity: 'Proximity',
}

export const LEAF_FAMILY: Record<CapabilityLeaf, CapabilityFamily> = {
  connect: 'connect',
  verify: 'auth', vault: 'auth',
  trace: 'trace',
  tamper: 'sense', temperature: 'sense', moisture: 'sense', pressure: 'sense', proximity: 'sense',
}

/**
 * Short, one-line description of what each capability does — shown as a tooltip when
 * hovering a capability badge (on cards, the product page, and the filter). Concise on
 * purpose (a tooltip, not the guide); the full guide copy lives on the product page.
 */
export const LEAF_BLURB: Record<CapabilityLeaf, string> = {
  connect: 'Every tap opens the item’s digital certificate.',
  verify: 'Verifies the item’s authenticity and ownership on every tap.',
  vault: 'A secure ownership & provenance record — for high-value resale.',
  tamper: 'A tamper-evident seal. Opening it is recorded permanently.',
  trace: 'Warehouse-scale tracking, readable at range with an RFID reader.',
  temperature: 'Logs temperature exposure — for cold-chain and freshness.',
  moisture: 'Detects moisture or humidity exposure.',
  pressure: 'Detects pressure or impact events.',
  proximity: 'Detects nearby tagged items.',
}

// Composite capability → ordered leaf set. Connect is prepended to every tag.
const LEAVES: Record<TagCapability, CapabilityLeaf[]> = {
  Connect: ['connect'],
  Verify: ['connect', 'verify'],
  'Verify + Tamper': ['connect', 'verify', 'tamper'],
  'Verify + Trace': ['connect', 'verify', 'trace'],
  'Verify + Vault': ['connect', 'verify', 'vault'],
  Temperature: ['connect', 'temperature'],
  Moisture: ['connect', 'moisture'],
  Pressure: ['connect', 'pressure'],
  Proximity: ['connect', 'proximity'],
}

/** Decompose a (possibly composite) capability into its ordered leaf badges. */
export function capabilityLeaves(capability: TagCapability): CapabilityLeaf[] {
  return LEAVES[capability] ?? ['connect']
}

/** Whether a capability carries cryptographic authenticity (Verify/Vault). */
export function isAuthCapability(capability: TagCapability): boolean {
  return capabilityLeaves(capability).some(l => LEAF_FAMILY[l] === 'auth')
}

/** Whether a capability is a Sense modality (no crypto — pairs with a Verify tag). */
export function isSenseCapability(capability: TagCapability): boolean {
  const leaves = capabilityLeaves(capability)
  return leaves.some(l => LEAF_FAMILY[l] === 'sense') && !leaves.includes('verify')
}

/**
 * VRG-156 — the single "upgrade" leaf a combo adds on top of the Verify baseline
 * (null for Connect-only and Verify). Drives the product page's atomic multi-select:
 * a tag carries at most ONE upgrade on top of Verify.
 *   Verify + Tamper → 'tamper' · Verify + Trace → 'trace' · Verify + Vault → 'vault'
 *   Temperature → 'temperature' · Moisture → 'moisture' · Pressure → 'pressure'
 */
export function capabilityUpgradeLeaf(capability: TagCapability): CapabilityLeaf | null {
  const leaves = capabilityLeaves(capability)
  const upgrade = leaves.find(l => l !== 'connect' && l !== 'verify')
  return upgrade ?? null
}

// VRG-156 — one-line, plain-language description per capability combo (product page).
// Reuses the leaf blurbs' voice; composed for combos. NEVER mentions the chip.
// Every combo LEADS with the Connect baseline: a tap connects to the item's digital
// certificate. Upgrades add their effect after (VRG-156 item 4).
const CAPABILITY_DESCRIPTION: Record<TagCapability, string> = {
  Connect: "Every tap connects to the item's digital certificate.",
  Verify: "Every tap connects to the item's digital certificate and verifies its authenticity and ownership.",
  'Verify + Tamper': "Every tap connects to the item's digital certificate and verifies its authenticity and ownership, and a broken seal shows if it's been opened.",
  'Verify + Trace': "Every tap connects to the item's digital certificate and verifies its authenticity and ownership, plus warehouse-scale tracking with an RFID reader.",
  'Verify + Vault': "Every tap connects to the item's digital certificate and verifies its authenticity, and keeps a secure ownership and provenance record.",
  Temperature: "Every tap connects to the item's digital certificate, and senses temperature for cold-chain.",
  Moisture: "Every tap connects to the item's digital certificate, and senses humidity and freshness.",
  Pressure: "Every tap connects to the item's digital certificate, and senses pressure changes.",
  Proximity: "Every tap connects to the item's digital certificate, and locates the item at range.",
}

/** One-line description of a capability combo (product page). Falls back to the Verify blurb. */
export function capabilityDescription(capability: TagCapability): string {
  return CAPABILITY_DESCRIPTION[capability] ?? CAPABILITY_DESCRIPTION.Verify
}

// ─── Availability ────────────────────────────────────────────────────────────

/**
 * Capabilities we advertise but cannot yet fulfil, so they must not be buyable.
 *
 * These are silicon constraints, not roadmap softness:
 * - `Verify + Vault` needs NTAG X DNA
 * - `Temperature` needs the NXP NHS3152
 *
 * Neither part is in supply yet. StatusDetect-backed sensing (`Moisture`,
 * `Pressure`) IS available on NTAG 22x DNA and stays purchasable -- so this is a
 * per-capability list, not "all sensing is coming soon".
 *
 * Kept here rather than in the shop components because the same fact gates three
 * surfaces (shop card, product page selector, cart) and a capability that is
 * blocked in one and buyable in another is an order we cannot ship. The seed
 * still lists these capabilities on purpose: they stay visible and legible as
 * demand signal, they just cannot be ordered.
 */
export const COMING_SOON_CAPABILITIES: readonly TagCapability[] = [
  'Verify + Vault',
  'Temperature',
]

export const isCapabilityComingSoon = (capability: TagCapability): boolean =>
  COMING_SOON_CAPABILITIES.includes(capability)

/**
 * The leaf badges to mark coming-soon, derived from the capability list rather than
 * hand-listed: only the UPGRADE leaf is unavailable. `Verify + Vault` still connects
 * and verifies on shipping silicon -- it is the `vault` leaf that needs NTAG X DNA --
 * so greying the whole badge row would misstate what is blocked.
 */
export const COMING_SOON_LEAVES: readonly CapabilityLeaf[] = COMING_SOON_CAPABILITIES
  .map(capabilityUpgradeLeaf)
  .filter((l): l is CapabilityLeaf => l !== null)

export const isLeafComingSoon = (leaf: CapabilityLeaf): boolean =>
  COMING_SOON_LEAVES.includes(leaf)

/**
 * Why a coming-soon capability can't be ordered, and what to do about it.
 * Names the route forward (§27: a gate is only useful if it is actionable).
 */
export const comingSoonReason = (label: string): string =>
  `${label} isn't available to order yet. Contact support to talk through your use case and we'll let you know as soon as it ships.`

/**
 * VRG-630 — FORMS we advertise but cannot yet fulfil, so they must not be buyable.
 *
 * ⚠️ THIS IS A SECOND GRAIN, NOT A DUPLICATE OF THE LIST ABOVE, AND THE
 * CAPABILITY LIST CANNOT EXPRESS IT. `ClosureLoop` offers `Verify + Tamper`
 * alone — a capability that IS in supply and IS orderable today on `Closure`.
 * The thing that does not exist is the twin-conductor cord BODY, which is a
 * fact about the form. Adding `Verify + Tamper` to COMING_SOON_CAPABILITIES to
 * reach it would block the Closure tags we sell.
 *
 * The rule the two lists share: a form or capability blocked on one surface and
 * buyable on another is an order we cannot ship, so the fact lives here once and
 * every surface reads it — shop card, product page selector, cart.
 *
 * ⚠️ `ClosureLoop` IS NOW ALSO HIDDEN — see `HIDDEN_FORMS` below. This paragraph
 * used to end "Deliberately NOT hidden… it stays visible and legible as demand
 * signal", and that is no longer the behaviour (Spencer, 2026-10-31). The two
 * lists stay SEPARATE rather than merging, because they answer different
 * questions: coming-soon says a form cannot be ORDERED, hidden says it cannot be
 * SEEN, and a form can be the first without being the second — that was this
 * form's own state until today, and is the state any future advertised-but-
 * unfulfillable form starts in.
 */
export const COMING_SOON_FORMS: readonly TagFormFactor[] = [
  'ClosureLoop',
]

export const isFormComingSoon = (form: TagFormFactor): boolean =>
  COMING_SOON_FORMS.includes(form)

/**
 * FORMS the shop does not show at all — draft, in the shop's sense.
 *
 * `ClosureLoop` (Spencer, 2026-10-31). It was visible-but-unorderable as demand
 * signal, and the demand-signal argument lost to what the tile actually looked
 * like: the part is not manufactured, so there is no photograph and no model of
 * it (`variantImagery.ts` — a form is never pointed at a neighbour's shot), and
 * a catalogue tile with no picture of its own reads as a broken product rather
 * than as a forthcoming one.
 *
 * ⚠️ HIDING IS NOT A SUBSTITUTE FOR THE COMING-SOON GATE, so the form stays in
 * `COMING_SOON_FORMS` too. Hiding governs the GRID; the gate governs the BUY, and
 * a deep link to the product page reaches it without passing the grid. Dropping
 * the gate on the grounds that nothing links there any more would make the one
 * route that still exists the one route with no protection.
 */
export const HIDDEN_FORMS: readonly TagFormFactor[] = [
  'ClosureLoop',
]

export const isFormHidden = (form: TagFormFactor): boolean =>
  HIDDEN_FORMS.includes(form)
